// The change list a reviewer actually reads: every element the commit
// touched, git-style, in four buckets that answer the four questions
// people ask - what appeared, what went, what was reshaped, what merely
// moved. Plus the fragments and tiles behind them, and a way to turn
// what you are looking at into an issue without leaving the view.
//
// It renders BESIDE the canvas rather than inside it: the viewer owns
// the picture, this owns the reading of it, and clicking a row asks the
// viewer to isolate that element.
import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Segmented, Tag, Tooltip, Empty, message } from "antd";
import { AimOutlined, PlusCircleOutlined, BlockOutlined } from "@ant-design/icons";
import { CHANGE_COLORS, CHANGE_LABELS, diffElementRows, diffTileRows, diffCoverageNote } from "helpers/diffOverlay";
import { renderFragmentThumbnail } from "helpers/fragmentThumbnail";
import { resolveFragmentUrl } from "helpers/fragmentUrl";

const BUCKETS = ["added", "removed", "modified", "moved"];

export default function DiffElementPanel({
  diff,
  onIsolate,          // (uniqueId) => void - ask the viewer to show just this
  // The way back out of that. Isolating HIDES the rest of the model, so
  // without this the only route back was reloading the page. The panel
  // shows the reset whenever `isolatedId` says something is isolated.
  onShowEverything,   // () => void
  isolatedId = null,
  // (bucket|null) => void - the 3D lights that bucket and ghosts the
  // rest, so the model answers the same question the list is filtered to.
  onBucketChange,
  // Optional: GET /commits/:id/edit-fragments, which carries the
  // fragment url + placement for BOTH generations. With it the panel can
  // draw a before/after picture of whichever row is picked; without it
  // the panel behaves exactly as it did.
  editFragments,
  onCreateIssue,      // () => Promise - snapshot the view and open a new issue
  creatingIssue = false,
  // var(--vh), not raw vh: the app runs at zoom 0.8, so 1vh is 0.8 of
  // what the layout means by it. App.scss declares --vh as the corrected
  // unit and a test enforces it repo-wide.
  height = "calc(70 * var(--vh))",
}) {
  const [tab, setTab] = useState("elements");
  const [filter, setFilter] = useState("");
  const [only, setOnly] = useState(null);
  const [picked, setPicked] = useState(null);
  const [shots, setShots] = useState({ old: null, new: null, loading: false });

  // Fragment lookup for the picked row. Keyed by uniqueId because that is
  // the only id both the diff rows and the fragment payload share.
  const fragById = useMemo(() => {
    const m = new Map();
    for (const f of (editFragments && editFragments.fragments) || []) m.set(String(f.uniqueId), f);
    return m;
  }, [editFragments]);

  // Draw the two pictures from the fragments themselves: a deleted
  // element is not in the current model, and the earlier generation of a
  // modified one only exists in an older export - neither can be
  // captured from the canvas next door.
  useEffect(() => {
    let cancelled = false;
    const f = picked ? fragById.get(String(picked)) : null;
    if (!f) { setShots({ old: null, new: null, loading: false }); return undefined; }
    const versions = (editFragments && editFragments.versions) || {};
    // A picture needs the shape, not the placement - so fall back to the
    // hash-resolved shape when the generation's own export is gone.
    const urlFor = (side) => {
      const rel = (f[side] && f[side].instance && f[side].instance.url)
        || (f[side] && f[side].shape && f[side].shape.url);
      return rel ? resolveFragmentUrl(rel, versions[side] && versions[side].manifestKey) : "";
    };
    setShots({ old: null, new: null, loading: true });
    Promise.all([
      renderFragmentThumbnail(urlFor("old"), { size: 116, color: "#9B7FD4" }),
      renderFragmentThumbnail(urlFor("new"), { size: 116, color: "#5B8DEF" }),
    ]).then(([o, n]) => { if (!cancelled) setShots({ old: o, new: n, loading: false }); });
    return () => { cancelled = true; };
  }, [picked, fragById, editFragments]);

  const rows = useMemo(() => diffElementRows(diff), [diff]);
  // Exact counts vs the rows actually listed (helpers/diffOverlay).
  const coverageNote = useMemo(() => diffCoverageNote(diff), [diff]);
  const tiles = useMemo(() => diffTileRows(diff), [diff]);

  const counts = useMemo(() => {
    const c = { added: 0, removed: 0, modified: 0, moved: 0 };
    rows.forEach((r) => { c[r.change] = (c[r.change] || 0) + 1; });
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (only && r.change !== only) return false;
      if (!q) return true;
      return [r.category, r.family, r.type, r.level, r.uniqueId, r.tileKey]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, filter, only]);

  const totalTiles = tiles.reduce((n, t) => n + t.count, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", height, minWidth: 320 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <Segmented
          size="small"
          value={tab}
          onChange={setTab}
          options={[
            { label: `Elements (${rows.length})`, value: "elements" },
            { label: `Fragments (${totalTiles})`, value: "tiles" },
          ]}
        />
        <span style={{ flex: 1 }} />
        {onCreateIssue && (
          <Tooltip title="Take a snapshot of the view as it stands and start an issue from it, with the screenshot attached.">
            <Button
              size="small"
              type="primary"
              icon={<PlusCircleOutlined />}
              loading={creatingIssue}
              onClick={onCreateIssue}
            >
              Create issue
            </Button>
          </Tooltip>
        )}
      </div>

      {coverageNote ? (
        <div style={{ background: "#FFF7E6", border: "1px solid #FFD591", color: "#874D00", borderRadius: 4, padding: "5px 8px", fontSize: 12, marginBottom: 8 }}>
          {coverageNote}
        </div>
      ) : null}

      {tab === "elements" ? (
        <>
          <div style={{ display: "flex", gap: 4, marginBottom: 6, flexWrap: "wrap" }}>
            {BUCKETS.map((b) => (
              <Tag
                key={b}
                onClick={() => {
                  const next = only === b ? null : b;
                  setOnly(next);
                  if (onBucketChange) onBucketChange(next);
                }}
                style={{
                  cursor: "pointer", margin: 0,
                  borderColor: CHANGE_COLORS[b],
                  background: only === b ? CHANGE_COLORS[b] : "transparent",
                  color: only === b ? "#fff" : "inherit",
                }}
              >
                <span
                  style={{
                    display: "inline-block", width: 8, height: 8, borderRadius: 2,
                    background: CHANGE_COLORS[b], marginRight: 6,
                  }}
                />
                {CHANGE_LABELS[b]} {counts[b] || 0}
              </Tag>
            ))}
          </div>
          {counts.removed > 0 && (
            <div style={{
              fontSize: 11, color: "#8a6d3b", background: "#fffbe6",
              border: "1px solid #ffe58f", borderRadius: 4, padding: "5px 8px", marginBottom: 6, lineHeight: 1.4,
            }}>
              Deletions are <b>inferred from absence</b> - the baseline had the
              element and this export does not. An element that failed to
              export looks the same, so treat these as "not in this export"
              until confirmed.
              {diff && diff.scope === "selection" && (
                <> This is a <b>selection push</b>, which cannot testify to deletions at all.</>
              )}
            </div>
          )}
          {isolatedId && onShowEverything && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginBottom: 6,
              padding: "5px 8px", borderRadius: 4,
              background: "#F3F7F9", border: "1px solid #D8E3E9",
            }}>
              <span style={{ flex: 1, fontSize: 11.5, color: "#3A4952", lineHeight: 1.35 }}>
                Showing <b>one element</b> - the rest of the model is hidden.
              </span>
              <Button size="small" onClick={onShowEverything}>Show everything</Button>
            </div>
          )}
          <Input
            size="small"
            allowClear
            placeholder="Filter by category, family, type, level, tile"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ marginBottom: 6 }}
          />
          {picked && editFragments && (
            <div style={{
              display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6,
              padding: 8, border: "1px solid #eee", borderRadius: 4, background: "#FCFCFC",
            }}>
              {["old", "new"].map((side) => (
                <div key={side}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: "#8D8D8D", letterSpacing: ".06em", marginBottom: 3 }}>
                    {side === "old" ? "BEFORE" : "AFTER"}
                  </div>
                  <div style={{
                    width: 116, height: 116, border: "1px solid #eee", borderRadius: 4, background: "#fff",
                    borderTop: `3px solid ${side === "old" ? "#9B7FD4" : "#5B8DEF"}`,
                    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                  }}>
                    {shots[side]
                      ? <img src={shots[side]} alt={side} style={{ maxWidth: "100%", maxHeight: "100%" }} />
                      : <span style={{ fontSize: 10.5, color: "#999", textAlign: "center", padding: 8 }}>
                          {shots.loading ? "rendering…" : side === "old" ? "no earlier geometry" : "no geometry"}
                        </span>}
                  </div>
                </div>
              ))}
              <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                Drawn from the fragment itself, in its own local space — a change of shape
                reads directly, and something that only moved looks identical in both frames.
                <div style={{ marginTop: 6 }}>
                  <Button size="small" onClick={(e) => { e.stopPropagation(); setPicked(null); }}>Close</Button>
                </div>
              </div>
            </div>
          )}
          <div style={{ flex: 1, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
            {shown.length === 0 ? (
              <Empty
                style={{ margin: 20 }}
                description={rows.length ? "Nothing matches that filter" : "This commit changed nothing"}
              />
            ) : (
              shown.map((r) => (
                <div
                  key={`${r.change}-${r.uniqueId}`}
                  onClick={() => setPicked(picked === r.uniqueId ? null : r.uniqueId)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer",
                    padding: "6px 8px", borderBottom: "1px solid #f5f5f5", fontSize: 12,
                    background: picked === r.uniqueId ? "#F3F7F9" : "transparent",
                  }}
                >
                  <span
                    title={CHANGE_LABELS[r.change]}
                    style={{
                      width: 10, height: 10, borderRadius: 2, marginTop: 3, flex: "0 0 auto",
                      background: CHANGE_COLORS[r.change],
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.family || r.category || "Element"}
                      {r.type ? ` · ${r.type}` : ""}
                    </div>
                    <div style={{ color: "#888" }}>
                      {[r.category, r.level].filter(Boolean).join(" · ")}
                      {r.tileKey ? ` · tile ${r.tileKey}` : ""}
                    </div>
                    {r.why.length > 0 && (
                      <div style={{ marginTop: 2 }}>
                        {r.why.map((w) => (
                          <Tag key={w} style={{ fontSize: 10, lineHeight: "16px", marginRight: 4 }}>{w}</Tag>
                        ))}
                      </div>
                    )}
                  </div>
                  {r.inferred && (
                    <Tooltip title="Inferred: present in the baseline, absent here. A failed export looks identical to a deletion.">
                      <Tag style={{ fontSize: 9, lineHeight: "15px", margin: 0 }}>inferred</Tag>
                    </Tooltip>
                  )}
                  {onIsolate && r.change !== "removed" && (
                    <Tooltip title={String(isolatedId || "") === String(r.uniqueId)
                      ? "Showing only this element - click again to show everything"
                      : "Show just this element in the view"}>
                      <Button
                        size="small"
                        type={String(isolatedId || "") === String(r.uniqueId) ? "primary" : "text"}
                        icon={<AimOutlined />}
                        onClick={() => (String(isolatedId || "") === String(r.uniqueId)
                          ? (onShowEverything ? onShowEverything() : onIsolate(null))
                          : onIsolate(r.uniqueId))}
                      />
                    </Tooltip>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <div style={{ flex: 1, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
          {tiles.length === 0 ? (
            <Empty style={{ margin: 20 }} description="No fragment or tile changes recorded" />
          ) : (
            tiles.map((t) => (
              <div key={t.category} style={{ padding: "8px 10px", borderBottom: "1px solid #f5f5f5" }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  <BlockOutlined /> {t.category}{" "}
                  <span style={{ color: "#888", fontWeight: 400 }}>
                    {t.count} tile{t.count === 1 ? "" : "s"}
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {t.tiles.map((k) => (
                    <Tag key={k} style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}>{k}</Tag>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
